import type { RebalanceMetrics } from "../../../api/src/rebalances/rebalances.service";
import {
  deserializeAllocationDetailPercents,
  deserializeRiskAllocationPercents,
  percentToRatio,
} from "../clients/percents";
import type {
  AccountRecord,
  CompletionAccountDetail,
  CompletionData,
  CompletionResidualModel,
  CompletionVerification,
  ExplainResponseV3,
  FinalData,
  ModelAssignmentData,
  OvershootRedistributionData,
  RequestData,
  TriggerData,
} from "./explain/lib/explainTypes";

/**
 * The rebalance handlers that return metrics send their percents on 0 to 100
 * (`an-decision-01a0ef4b7e9377c9` row 1; `rebalanceMetricPercents`,
 * `api/src/rebalances/rebalances.controller.ts`). The deserializers here
 * divide each by 100 where the console reads the response, so every screen
 * keeps computing and displaying from the 0 to 1 ratio it always read:
 * `getRiskAllocationDisplay` and `getRiskAllocation` (`./lib`), which
 * `AdminRebalanceList` also calls on `GET /admin/rebalances` (0 to 1,
 * unconverted), and the rebalance the console builds from
 * `POST /rebalances/manual/xlsx` (`buildManualRebalance`; 0 to 1,
 * unconverted, where `POST /v1/rebalances/manual` sends 0 to 100).
 *
 * `reasonChange` is on 0 to 100 only for a `reason` that ends in "Allocation"
 * (`isFractionReason`, `api/src/rebalances/rebalances.controller.ts`), so
 * `formatDrift` and `toleranceBandSubLabel` (`./RebalancesToday`) keep
 * reading the fraction they show x100, and the dollars of `Cash` as sent.
 */

/** The `RebalanceMetrics` keys that carry a percent. */
type MetricPercentKeys = {
  assetAllocation?: RebalanceMetrics["assetAllocation"];
  riskAllocation?: RebalanceMetrics["riskAllocation"];
  reason?: RebalanceMetrics["reason"];
  reasonChange?: RebalanceMetrics["reasonChange"];
  postRebalanceHoldings?: {
    security: { riskAllocation?: Record<string, number> } | null;
  }[];
};

/**
 * `true` for a `reason` whose `reasonChange` the API sends on 0 to 100: the
 * test the API makes (`isFractionReason`,
 * `api/src/rebalances/rebalances.controller.ts`).
 */
function isFractionReason(reason: string | null | undefined): boolean {
  return typeof reason === "string" && /Allocation$/.test(reason);
}

/**
 * `metrics` with `assetAllocation[].allocation`, each risk level of
 * `riskAllocation.current`, `.target` and `.postRebalance`, each
 * `postRebalanceHoldings[].security.riskAllocation`, and `reasonChange` where
 * `reason` makes it a percent, on 0 to 1. A `null` key stays `null`, and a
 * holding whose `security.riskAllocation` is `null` (a security the account
 * buys and did not hold) stays as sent.
 */
function deserializeMetricPercents<TMetrics extends object>(
  sent: TMetrics,
): TMetrics {
  const metrics = sent as MetricPercentKeys;
  const {
    assetAllocation,
    riskAllocation,
    reasonChange,
    postRebalanceHoldings,
  } = metrics;
  return {
    ...sent,
    ...(typeof reasonChange === "number" && isFractionReason(metrics.reason)
      ? { reasonChange: percentToRatio(reasonChange) }
      : {}),
    ...(Array.isArray(postRebalanceHoldings)
      ? {
          postRebalanceHoldings: postRebalanceHoldings.map((holding) =>
            holding.security?.riskAllocation == null
              ? holding
              : {
                  ...holding,
                  security: {
                    ...holding.security,
                    riskAllocation: deserializeRiskAllocationPercents(
                      holding.security.riskAllocation,
                    ),
                  },
                },
          ),
        }
      : {}),
    ...(assetAllocation == null
      ? {}
      : {
          assetAllocation: deserializeAllocationDetailPercents(assetAllocation),
        }),
    ...(riskAllocation == null
      ? {}
      : {
          riskAllocation: {
            current: deserializeRiskAllocationPercents(riskAllocation.current),
            target: deserializeRiskAllocationPercents(riskAllocation.target),
            postRebalance: deserializeRiskAllocationPercents(
              riskAllocation.postRebalance,
            ),
          },
        }),
  };
}

/** `rebalance`'s metric percents, and each account's, on 0 to 1. */
export function deserializeRebalanceMetricPercents<
  TRebalance extends { accounts: object[] },
>(rebalance: TRebalance): TRebalance {
  return {
    ...deserializeMetricPercents(rebalance),
    accounts: rebalance.accounts.map(deserializeMetricPercents),
  };
}

/** Each rebalance's metric percents on 0 to 1. */
export function deserializeRebalanceListPercents<
  TRebalance extends { accounts: object[] },
>(rebalances: TRebalance[]): TRebalance[] {
  return rebalances.map(deserializeRebalanceMetricPercents);
}

/*
 * The explanation of a rebalance (`GET /v1/rebalances/:id/explain`) arrives
 * with its weights on 0 to 100, each a decimal string still
 * (`explanationPercents`, `api/src/rebalances/rebalances.controller.ts`,
 * APP-1652). `deserializeExplanationPercents` gives back the worker's 0 to 1
 * text, digit for digit, where `useExplain` reads it, so the explanation
 * screens, which show some of these strings as they are (`TriggerPanel`,
 * `RestrictionsSection`, `SubstitutionsSection`), show what they showed
 * before. `GET /admin/rebalances/:id/explain` and
 * `POST /rebalances/manual/xlsx` send 0 to 1 unconverted and are not read
 * through it.
 */

/** A decimal string: sign, digits with an optional point, and an exponent. */
const DECIMAL_STRING = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/**
 * `value` (0 to 100) on 0 to 1, as the API's `decimalRatioToPercent`
 * (`api/src/lib/client-percents.ts`) undoes: the API added two zeros to the
 * digits and kept the decimal places, so the two zeros are taken off, or the
 * places grow where the digits end in fewer zeros. The text is written as
 * Python's `str(Decimal)` writes it, so `"8.400"` gives back the worker's
 * `"0.084"` and `"0.0000100"` its `"1E-7"`. A value that is not a decimal
 * string is returned unchanged.
 */
export function decimalPercentToRatio(value: string): string {
  const match = DECIMAL_STRING.exec(value);
  if (match === null) {
    return value;
  }
  const [, sign, whole = "", fraction = "", exponentText = "0"] = match;
  if (whole === "" && fraction === "") {
    return value;
  }
  let coefficient = `${whole}${fraction}`.replace(/^0+(?=\d)/, "");
  let exponent = Number(exponentText) - fraction.length;
  if (coefficient !== "0") {
    // Divide by 100: take off up to two trailing zeros, and move the
    // exponent down for each one that is not there.
    const zeros = Math.min(
      2,
      coefficient.length - coefficient.replace(/0+$/, "").length,
    );
    coefficient = coefficient.slice(0, coefficient.length - zeros);
    exponent -= 2 - zeros;
  }
  const negative = sign === "-" ? "-" : "";
  const adjusted = exponent + coefficient.length - 1;
  if (exponent <= 0 && adjusted >= -6) {
    if (exponent === 0) {
      return `${negative}${coefficient}`;
    }
    const point = coefficient.length + exponent;
    return point > 0
      ? `${negative}${coefficient.slice(0, point)}.${coefficient.slice(point)}`
      : `${negative}0.${"0".repeat(-point)}${coefficient}`;
  }
  const mantissa =
    coefficient.length > 1
      ? `${coefficient[0]}.${coefficient.slice(1)}`
      : coefficient;
  return `${negative}${mantissa}E${adjusted >= 0 ? "+" : ""}${adjusted}`;
}

/** `value` on 0 to 1 if it is a decimal string, else unchanged. */
function ratioOf<TValue>(value: TValue): TValue {
  return (
    typeof value === "string" ? decimalPercentToRatio(value) : value
  ) as TValue;
}

/** Each value of `weights` on 0 to 1. `null` or absent stays so. */
function ratioMap<TMap extends Record<string, string> | null | undefined>(
  weights: TMap,
): TMap {
  return (
    weights == null
      ? weights
      : Object.fromEntries(
          Object.entries(weights).map(([key, weight]) => [
            key,
            ratioOf(weight),
          ]),
        )
  ) as TMap;
}

/** `object`, or `null`, with `convert` applied to it when it is an object. */
function ifPresent<TObject>(
  object: TObject | null,
  convert: (present: TObject) => TObject,
): TObject | null {
  return object == null ? object : convert(object);
}

/** The trigger's weights, and its drift where `reason` makes it one, on 0 to 1. */
function deserializeTriggerPercents(trigger: TriggerData): TriggerData {
  const fraction = isFractionReason(trigger.reason);
  return {
    ...trigger,
    reason_detail: fraction
      ? ratioOf(trigger.reason_detail)
      : trigger.reason_detail,
    severity_pct: fraction
      ? ratioOf(trigger.severity_pct)
      : trigger.severity_pct,
    allocation_drift: trigger.allocation_drift.map((row) => ({
      ...row,
      current: ratioOf(row.current),
      target: ratioOf(row.target),
      delta: ratioOf(row.delta),
    })),
  };
}

/** `model.primary` on 0 to 1. */
function deserializeModelPercents<
  TModel extends { primary: Record<string, string> },
>(model: TModel): TModel {
  return { ...model, primary: ratioMap(model.primary) };
}

/** The household model's `primary` on 0 to 1. */
function deserializeRequestPercents(request: RequestData): RequestData {
  return {
    ...request,
    household_model: ifPresent(
      request.household_model,
      deserializeModelPercents,
    ),
  };
}

/** The account's weights on 0 to 1: those `explanationPercents` converts. */
function deserializeAccountRecordPercents(
  account: AccountRecord,
): AccountRecord {
  return {
    ...account,
    model: deserializeModelPercents(account.model),
    cash_adjustment: ifPresent(account.cash_adjustment, (cash) => ({
      ...cash,
      target: { ...cash.target, weight: ratioOf(cash.target.weight) },
      band: {
        ...cash.band,
        min: ratioOf(cash.band.min),
        max: ratioOf(cash.band.max),
      },
      adjustment_pp: ratioOf(cash.adjustment_pp),
    })),
    restriction_adjustment: ifPresent(
      account.restriction_adjustment,
      (restriction) => ({
        ...restriction,
        restrictions: restriction.restrictions.map((entry) => ({
          ...entry,
          locked_weight: ratioOf(entry.locked_weight),
        })),
      }),
    ),
    secondary_securities: ifPresent(
      account.secondary_securities,
      (secondary) => ({
        ...secondary,
        substitutions: secondary.substitutions.map((entry) => ({
          ...entry,
          weight_transferred: ratioOf(entry.weight_transferred),
        })),
      }),
    ),
    constrained_allocation: ifPresent(
      account.constrained_allocation,
      (constrained) => ({
        ...constrained,
        final_target: ratioMap(constrained.final_target),
        adjustments: constrained.adjustments.map((row) => ({
          ...row,
          target_before: ratioOf(row.target_before),
          target_after: ratioOf(row.target_after),
          intended_target: ratioOf(row.intended_target),
        })),
        current_weights: ratioMap(constrained.current_weights),
      }),
    ),
    checkpoints: Object.fromEntries(
      Object.entries(account.checkpoints).map(([key, checkpoint]) => [
        key,
        checkpoint == null
          ? checkpoint
          : { ...checkpoint, by_symbol: ratioMap(checkpoint.by_symbol) },
      ]),
    ),
    achieved_risk_weights: ratioMap(account.achieved_risk_weights),
    target_risk_weights: ratioMap(account.target_risk_weights),
  };
}

/** The household's risk weights and deltas on 0 to 1. */
function deserializeFinalPercents(final: FinalData): FinalData {
  return {
    ...final,
    target_risk_weights: ratioMap(final.target_risk_weights),
    achieved_risk_weights: ratioMap(final.achieved_risk_weights),
    risk_deltas: ratioMap(final.risk_deltas),
    asset_class_deltas: ratioMap(final.asset_class_deltas),
  };
}

/*
 * `model_assignment.completion` on 0 to 1 again: the keys `completionPercents`
 * (`api/src/rebalances/rebalances.controller.ts`) sends on 0 to 100, which
 * `CompletionStrategyPanel` and the soft-completion sections read as 0 to 1.
 * A key an older payload lacks stays absent.
 */

/** `verification`'s deviations and constraint weights on 0 to 1. */
function deserializeVerificationPercents(
  verification: CompletionVerification,
): CompletionVerification {
  return {
    ...verification,
    max_deviation: ratioOf(verification.max_deviation),
    deviations_by_asset_class: ratioMap(verification.deviations_by_asset_class),
    deviations_by_symbol: ratioMap(verification.deviations_by_symbol),
    ...(verification.constraint_impacts == null
      ? {}
      : {
          constraint_impacts: verification.constraint_impacts.map((impact) => ({
            ...impact,
            target_weight: ratioOf(impact.target_weight),
            actual_weight: ratioOf(impact.actual_weight),
            deviation: ratioOf(impact.deviation),
          })),
        }),
  };
}

/** A residual model's weights and model weights on 0 to 1. */
function deserializeResidualModelPercents(
  model: CompletionResidualModel,
): CompletionResidualModel {
  return {
    ...model,
    residual_target_weights: ratioMap(model.residual_target_weights),
    residual_calculation: ifPresent(model.residual_calculation, (calc) => ({
      ...calc,
      ...(calc.security_mapping_rows == null
        ? {}
        : {
            security_mapping_rows: calc.security_mapping_rows.map((row) => ({
              ...row,
              model_weight: ratioOf(row.model_weight),
            })),
          }),
    }))!,
  };
}

/** An account detail's share, risk maps and model maps on 0 to 1. */
function deserializeAccountDetailPercents(
  detail: CompletionAccountDetail,
): CompletionAccountDetail {
  const optional = <TKey extends keyof CompletionAccountDetail>(
    key: TKey,
    convert: (
      value: NonNullable<CompletionAccountDetail[TKey]>,
    ) => CompletionAccountDetail[TKey],
  ) =>
    detail[key] == null
      ? {}
      : {
          [key]: convert(
            detail[key] as NonNullable<CompletionAccountDetail[TKey]>,
          ),
        };
  return {
    ...detail,
    pct_of_household: ratioOf(detail.pct_of_household),
    household_risk: ratioMap(detail.household_risk),
    constrained_risk: ratioMap(detail.constrained_risk),
    residual_risk: ratioMap(detail.residual_risk),
    original_model: ratioMap(detail.original_model),
    filtered_model: ratioMap(detail.filtered_model),
    ...optional("constrained_model", ratioMap),
    ...optional("after_cap_model", ratioMap),
    ...optional("capped_to_pct", ratioOf),
    ...optional("leftover_pct", ratioOf),
  };
}

/** Each `redeploy[].freed_pct` on 0 to 1. */
function deserializeOvershootPercents(
  overshoot: OvershootRedistributionData,
): OvershootRedistributionData {
  return overshoot.redeploy == null
    ? overshoot
    : {
        ...overshoot,
        redeploy: overshoot.redeploy.map((entry) => ({
          ...entry,
          freed_pct: ratioOf(entry.freed_pct),
        })),
      };
}

/** Each value of a map of `convert`ed entries. */
function mapEntries<TEntry>(
  entries: Record<string, TEntry>,
  convert: (entry: TEntry) => TEntry,
): Record<string, TEntry> {
  return Object.fromEntries(
    Object.entries(entries).map(([key, entry]) => [key, convert(entry)]),
  );
}

/** `completion` with the weights `completionPercents` converts on 0 to 1. */
function deserializeCompletionPercents(
  completion: CompletionData,
): CompletionData {
  const soft = completion as CompletionData & {
    overshoot_redistribution?: OvershootRedistributionData | null;
  };
  return {
    ...completion,
    ...(completion.verification == null
      ? {}
      : {
          verification: deserializeVerificationPercents(
            completion.verification,
          ),
        }),
    ...(completion.residual_models == null
      ? {}
      : {
          residual_models: mapEntries(
            completion.residual_models,
            deserializeResidualModelPercents,
          ),
        }),
    ...(completion.account_details == null
      ? {}
      : {
          account_details: mapEntries(
            completion.account_details,
            deserializeAccountDetailPercents,
          ),
        }),
    ...(soft.overshoot_redistribution == null
      ? {}
      : {
          overshoot_redistribution: deserializeOvershootPercents(
            soft.overshoot_redistribution,
          ),
        }),
  };
}

/** `model_assignment` with its `completion` on 0 to 1. */
function deserializeModelAssignmentPercents(
  assignment: ModelAssignmentData,
): ModelAssignmentData {
  return {
    ...assignment,
    completion: ifPresent(assignment.completion, deserializeCompletionPercents),
  };
}

/**
 * `explanation` with each weight `explanationPercents` sends on 0 to 100 on 0
 * to 1 again, in the worker's text.
 */
export function deserializeExplanationPercents(
  explanation: ExplainResponseV3,
): ExplainResponseV3 {
  return {
    ...explanation,
    trigger: deserializeTriggerPercents(explanation.trigger),
    request: deserializeRequestPercents(explanation.request),
    model_assignment: deserializeModelAssignmentPercents(
      explanation.model_assignment,
    ),
    accounts: explanation.accounts.map(deserializeAccountRecordPercents),
    final: deserializeFinalPercents(explanation.final),
  };
}
